import type { Numeric } from 'decimal.js-light';
import Decimal from 'decimal.js-light';

import { formatFixed } from './format-fixed';

/**
 * Format a number with thousands separators and 2 decimal places.
 *
 * @param value The value to format. Accepts string, number, or Decimal.
 *
 * @example
 * formatMoney(1234.567)  // returns '1,234.57'
 * formatMoney(-1234.59)  // returns '-1,234.59'
 * formatMoney('12345.6') // returns '12,345.60'
 */
export function formatMoney(
	value: Numeric,
	options?: { addDollarSign?: boolean; rounded?: boolean }
): string {
	const formatted = formatFixed(value, options?.rounded ? 0 : 2);

	if (options?.addDollarSign) {
		if (formatted.startsWith('-')) {
			return `-$${formatted.slice(1)}`;
		} else {
			return `$${formatted}`;
		}
	}

	return formatted;
}

/**
 * Format a number with thousands separators and 2 decimal places.
 * Adds parentheses for negative numbers instead of a negative sign.
 *
 * @param value The value to format. Accepts string, number, or Decimal.
 * @param options.invert If `true`, invert the sign of the result
 * (and show 0 as negative). Defaults to `false`.
 *
 * @example
 * formatMoneyWithParens(1234.567)   // returns '1,234.57'
 * formatMoneyWithParens(-1234.59)   // returns '(1,234.59)'
 * formatMoneyWithParens('-12345.6') // returns '(12,345.60)'
 *
 * formatMoneyWithParens('1234.56', { invert: true }) // returns '(1,234.56)'
 * formatMoneyWithParens('0', { invert: true })       // returns '(0.00)'
 * formatMoneyWithParens('-234.56', { invert: true }) // returns '234.56'
 */
export function formatMoneyWithParens(
	value: Numeric,
	options?: { invert?: boolean }
): string {
	if (value === undefined || value === null) {
		return '';
	}

	const invert = Boolean(options?.invert);

	let number = new Decimal(value);
	if (invert) number = number.times(-1);
	const isNegative = number.isNegative() || (invert && number.isZero());

	let result = formatMoney(number.absoluteValue());
	if (isNegative) result = `(${result})`;

	return result;
}
